import { useEffect } from 'react';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Skeleton from '@mui/material/Skeleton';
import Pagination from '@mui/material/Pagination';
import CardHeader from '@mui/material/CardHeader';
import Typography from '@mui/material/Typography';

import { fDateTime } from 'src/utils/format-time';

import { useAppDispatcher, useAppSelector } from 'src/redux/store';
import { getLoanAgreementAuditLog } from 'src/redux/slices/loan-agreement';

import Iconify from 'src/components/iconify';

import { AuditAction, IAuditListItem } from 'src/types/audit';

import { buildLoanActivityMessage } from './utils';

const ACTIVITY_LOG_PAGE_SIZE = 10;

type Props = {
  agreementId: string;
  // Bumped by the parent after any write so the log refetches.
  refreshKey?: number;
};

function activityIcon(item: IAuditListItem) {
  if (item.after?.status === 'bad_debt') return 'solar:danger-triangle-bold';
  switch (item.action) {
    case AuditAction.CREATED:
      return 'eva:plus-circle-fill';
    case AuditAction.DELETED:
      return 'eva:trash-2-fill';
    default:
      return 'eva:edit-2-fill';
  }
}

/**
 * Same feed as the employee activity log, scoped to one agreement and its
 * payments.
 */
export default function LoanAgreementActivityLog({ agreementId, refreshKey }: Props) {
  const dispatch = useAppDispatcher();

  const { status, count, page, pageCount, items } = useAppSelector(
    (state) => state.loanAgreement.auditLog
  ) as {
    status: 'idle' | 'loading' | 'success' | 'failure';
    count: number;
    page: number;
    pageCount: number;
    items: IAuditListItem[];
  };

  const isLoading = status === 'loading';

  useEffect(() => {
    dispatch(getLoanAgreementAuditLog({ id: agreementId, page: 1, limit: ACTIVITY_LOG_PAGE_SIZE }));
  }, [dispatch, agreementId, refreshKey]);

  const handleChangePage = (_: React.ChangeEvent<unknown>, newPage: number) => {
    dispatch(
      getLoanAgreementAuditLog({ id: agreementId, page: newPage, limit: ACTIVITY_LOG_PAGE_SIZE })
    );
  };

  return (
    <Card sx={{ p: 3 }}>
      <CardHeader title="Activity Log" sx={{ p: 0, mb: 2 }} />

      {isLoading && (
        <Stack spacing={2}>
          {[...Array(3)].map((_, index) => (
            <Skeleton key={index} variant="rounded" height={56} />
          ))}
        </Stack>
      )}

      {!isLoading && items.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No activity yet.
        </Typography>
      )}

      {!isLoading && items.length > 0 && (
        <Stack spacing={2}>
          {items.map((item) => (
            <Stack key={item._id} direction="row" spacing={2} alignItems="flex-start">
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  bgcolor: 'background.neutral',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Iconify icon={activityIcon(item)} width={18} />
              </Box>

              <Stack flexGrow={1} spacing={0.5}>
                <Typography variant="body2">
                  {buildLoanActivityMessage(item) ?? `${item.type} ${item.action.toLowerCase()}`}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                  {item.actorEmail}
                </Typography>
              </Stack>

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                {fDateTime(item.createdAt)}
              </Typography>
            </Stack>
          ))}
        </Stack>
      )}

      {count > 0 && (
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 3 }}>
          <Typography variant="caption" color="text.disabled">
            {count} {count === 1 ? 'activity' : 'activities'}
          </Typography>

          {pageCount > 1 && (
            <Pagination
              size="small"
              color="primary"
              page={page}
              count={pageCount}
              onChange={handleChangePage}
            />
          )}
        </Stack>
      )}
    </Card>
  );
}
